import { AxiosError, AxiosResponse } from 'axios';

enum ErrorCodes {
  Plain = 'PLAIN',
  Complex = 'COMPLEX',
}

export interface PlainResponse {
  statusCode: 'PLAIN';
  details: string;
}

interface ComplexResponse {
  statusCode: 'COMPLEX';
  details: object;
}

export const isResponsePlainError = (err: AxiosError<any>) => {
  return err.response && err.response.data.errorCode === ErrorCodes.Plain;
};

export const isResponseComplexError = (err: AxiosError<any>) => {
  return err.response && err.response.data.errorCode === ErrorCodes.Complex;
};

export const isCorrectResponse = function<TResponse>(
  res: AxiosResponse<TResponse> | undefined,
): res is AxiosResponse<TResponse> {
  return Boolean(res && res.data);
};

abstract class ResponseError<TResponse> extends Error {
  protected err: AxiosError<TResponse>;

  constructor(err: AxiosError<TResponse>) {
    super('Response Error');

    if (!isCorrectResponse(err.response)) {
      throw new Error(this.message);
    }

    this.err = err;

    Object.setPrototypeOf(this, ResponseError.prototype);
  }

  public gerError() {
    return this.err;
  }

  public getResponseData() {
    return (this.err.response as AxiosResponse<TResponse>).data;
  }

  abstract getErrorMessage(): string;
}

export class PlainResponseError extends ResponseError<PlainResponse> {
  constructor(err: AxiosError<PlainResponse>) {
    super(err);

    Object.setPrototypeOf(this, PlainResponseError.prototype);
  }

  getErrorMessage() {
    return super.getResponseData().details;
  }
}

export class ComplexResponseError extends ResponseError<ComplexResponse> {
  constructor(err: AxiosError<ComplexResponse>) {
    super(err);

    Object.setPrototypeOf(this, ComplexResponseError.prototype);
  }

  getErrorMessage() {
    return this.message;
  }
}

type Group = '1xx' | '2xx' | '3xx' | '4xx' | '5xx';

export const isStatusCodeGroup = (response: AxiosResponse, group: Group) => {
  const groupPrefix = group.replace('xx', '');
  return response.status.toString().startsWith(groupPrefix);
};

export const isConcreteErrorCode = (response: AxiosResponse, status: string) => {
  return response.status.toString() === status;
};
